import plusIcon from "@assets/images/plus.svg";
import { Button } from "@material-ui/core";
import Container from "@saleor/components/Container";
import MyTypography from "@saleor/components/MyTypography";
import PageHeader from "@saleor/components/PageHeader";
import { defaultListSettings } from "@saleor/config";
import {
  ScgpUsersFilterInput,
  useChangeScgpUserStatusMutation,
  useGetListUserManagementQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import useLocalPaginator, {
  useLocalPaginationState
} from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import { makeStyles } from "@saleor/macaw-ui";
import { ListViews } from "@saleor/types";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import UserListFilter from "../../components/UserListFilter";
import UserListTable from "../../components/UserListTable";
import { userDetailUrl, UserListUrl, UserListUrlQueryParams } from "../../urls";
import { getSortQueryUserListManagementVariables } from "./sort";

export interface UserListProps {
  params: UserListUrlQueryParams;
}

interface StyleProps {
  fullScreen?: boolean;
}

export enum StatusFilter {
  INFO = "INFO",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR"
}

const useStyles = makeStyles<StyleProps>(
  _ => ({
    pageHeader: {
      marginBottom: "1.2rem",
      "& > :first-child": {
        visibility: props => (props.fullScreen ? "hidden" : "visible"),
        margin: 0,
        marginBottom: props => (props.fullScreen ? "0.4rem" : "1.2rem")
      },
      "& > :nth-child(2)": {
        alignSelf: "flex-end"
      }
    },
    searchBar: {
      display: props => (props.fullScreen ? "none" : "block")
    },
    button: {
      minWidth: "16.4rem",
      "& img": {
        marginRight: "1rem"
      }
    },
    txtButton: {
      fontSize: "1.8rem"
    }
  }),
  { name: "UserList" }
);

const UserList: React.FC<UserListProps> = ({ params }) => {
  const intl = useIntl();
  const [{ fullScreen }] = useAppState();
  const classes = useStyles({ fullScreen });
  const navigate = useNavigator();
  const [filter, setFilter] = useState<ScgpUsersFilterInput>({});
  const [skip, setSkip] = useState<boolean>(true);

  const { updateListSettings, settings } = useListSettings(
    ListViews.USER_MANAGEMENT
  );
  const [updateStatusUser] = useChangeScgpUserStatusMutation();
  useEffect(() => {
    updateListSettings(
      "rowNumber",
      defaultListSettings.USER_MANAGEMENT.rowNumber
    );
  }, []);
  const handleSearch = value => {
    if (Object.keys(value).length) {
      setSkip(false);
    } else {
      setSkip(true);
    }
    setFilter(value);
  };

  const [
    valuesPaginationState,
    setValuesPaginationState
  ] = useLocalPaginationState(settings?.rowNumber);

  const resetPaginationState = () => {
    setValuesPaginationState({
      first: settings?.rowNumber,
      after: undefined,
      before: undefined
    });
  };

  const paginateValues = useLocalPaginator(setValuesPaginationState);

  const queryVariables = React.useMemo(
    () => ({
      ...valuesPaginationState,
      filter,
      sortBy: getSortQueryUserListManagementVariables(params)
    }),
    [settings?.rowNumber, valuesPaginationState, params]
  );

  const {
    data: dataSearch,
    loading: loadingSearch
  } = useGetListUserManagementQuery({
    skip,
    displayLoader: true,
    variables: queryVariables
  });

  const lastPaginationState = () => {
    setValuesPaginationState({
      last: dataSearch?.scgpUsers?.latestPageItemNumber
    });
  };

  const { loadNextPage, loadPreviousPage, pageInfo } = paginateValues(
    dataSearch?.scgpUsers?.pageInfo,
    valuesPaginationState
  );

  const handleSort = field => {
    resetPaginationState();
    navigate(
      UserListUrl({
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };
  const handlePreviousFirstPage = () => {
    resetPaginationState();
  };
  const handleNextLastPage = () => {
    lastPaginationState();
  };
  const viewUserDetail = (userId, params) => {
    navigate(userDetailUrl(userId, params));
  };

  const dataSearchList = useMemo(
    () =>
      Object.keys(filter).length
        ? mapEdgesToItems(dataSearch?.scgpUsers) || []
        : [],
    [dataSearch?.scgpUsers, filter]
  );

  const handleEditUser = event => {
    updateStatusUser({
      variables: {
        id: event.id,
        status: event.target.value
      }
    });
  };

  const getStatusFilter: StatusFilter = useMemo(() => {
    const listFilter = Object.keys(filter);
    if (!loadingSearch) {
      if (!listFilter.length) {
        return StatusFilter.INFO;
      } else {
        if (dataSearchList.length) {
          return StatusFilter.SUCCESS;
        } else {
          return StatusFilter.ERROR;
        }
      }
    } else {
      return StatusFilter.INFO;
    }
  }, [dataSearchList, loadingSearch]);

  useEffect(() => {
    resetPaginationState();
  }, [settings?.rowNumber, filter]);

  return (
    <Container>
      <PageHeader
        className={classes.pageHeader}
        title={intl.formatMessage({
          defaultMessage: "User Management",
          description: "label"
        })}
      >
        <Button
          variant="contained"
          color="secondary"
          className={classNames(classes.button, "defaultB")}
          onClick={() => navigate(userDetailUrl("create"))}
          id="addUserButton"
          name="addUserButton"
          data-testid="addUserButton"
        >
          <img src={plusIcon} alt="plus" />
          <MyTypography variant={"body1"} className={classes.txtButton}>
            {intl.formatMessage({ defaultMessage: "เพิ่มผู้ใช้งาน" })}
          </MyTypography>
        </Button>
      </PageHeader>
      <UserListFilter
        setFilter={handleSearch}
        statusFilter={getStatusFilter}
        dataSearchList={dataSearchList}
      />
      {loadingSearch || dataSearchList?.length ? (
        <UserListTable
          settings={settings}
          disabled={loadingSearch}
          data={dataSearchList}
          onNextPage={loadNextPage}
          onPreviousPage={loadPreviousPage}
          onUpdateListSettings={updateListSettings}
          onNextLastPage={handleNextLastPage}
          onPreviousFirstPage={handlePreviousFirstPage}
          onRowClick={() => null}
          totalCount={dataSearch?.scgpUsers?.totalCount}
          latestPageItemNumber={dataSearch?.scgpUsers?.latestPageItemNumber}
          pageInfo={pageInfo}
          loading={loadingSearch}
          onSort={handleSort}
          sort={getSortParams(params)}
          onViewUserDetail={viewUserDetail}
          onEditUser={handleEditUser}
          group={filter?.group}
        />
      ) : null}
    </Container>
  );
};

export default UserList;
